import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';

import { useHistory } from 'react-router-dom';
import { Icon, Loader } from 'semantic-ui-react';

import {
	handleError,
	log
} from '../../../../../../services/utils';

import {
	getTemplateList,
	deleteTemplate
} from '../../../../../../api/nora/programs';

import useToggle from '../../../../../../hooks/useToggle';

import { useAuthState } from '../../../../../../contexts/AuthContext';
import {
	useProgramState,
	useProgramAction
} from '../../../../../../contexts/ProgramsContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import Actions from '../../../../../Shared/Actions';
import Alert from '../../../../../Alert';
import HelpModal from '../../../../../Shared/HelpModal';
import MultiSelectColumnFilter from '../../../../../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../../../../Table/filterFunctions/multiSelectFilterFn';
import PageHeader from '../../../../../Shared/PageHeader';
import Table from '../../../../../Table';
import TableRowActions from '../../../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../../../Table/filterFunctions/multiWordFilterFn';

const TemplatesTable = () => {
	const history = useHistory();

	const [ isLoading, setIsLoading ] = useState( false );
	const [ tableLoading, setTableLoading ] = useState( false );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ templateToDelete, setTemplateToDelete ] = useState( {} );
	const [ errorAlert, toggleErrorAlert ] = useToggle( false );
	const [ errorAlertMessage, setErrorAlertMessage ] = useState( '' );

	const { user: { permissions } } = useAuthState();
	const {
		override,
		program,
		templates
	} = useProgramState();
	const { setTemplates } = useProgramAction();
	const { setInfobarLoader } = useUIAction();

	const retrieveTemplateList = useCallback( async () => {
		setInfobarLoader( 'Retrieving Templates...' );
		setTableLoading( true );

		try {
			const data = await getTemplateList( program.id );

			setTemplates( data );
		} catch ( error ) {
			const errorMessage = handleError( error );

			setErrorAlertMessage( errorMessage );
			toggleErrorAlert();
		}

		setInfobarLoader( '' );
		setTableLoading( false );
		setIsLoading( false );
	}, [
		program.id,
		setTemplates,
		setInfobarLoader,
		toggleErrorAlert
	] );

	const clearTemplates = useCallback( () => {
		setTemplates( [] );
	}, [ setTemplates ] );

	// is this OK???
	useEffect( () => {
		( async () => {
			await retrieveTemplateList();
		} )();

		return clearTemplates;
	}, [
		clearTemplates,
		retrieveTemplateList,
		setTemplates
	] );

	const onView = useCallback( template => {
		const path = override.path
			? `${ override.path }/templates/${ template.id }`
			: `/operations/programs/${ program.id }/templates/${ template.id }`;

		history.push( path );
	}, [
		history,
		override,
		program.id
	] );

	const onDelete = useCallback( template => {
		setTemplateToDelete( template );
		setDeleteConfirm( true );
	}, [] );

	const StatusCell = useCallback( ( { row } ) => (
		<span>
			{ row.original.status.name }
			&nbsp;
			{ row.original.isPaused
				? <Icon name="pause circle outline" />
				: null
			}
		</span>
	), [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewPrograms.canExecute,
				icon: 'search',
				permission: permissions.viewPrograms.isAvailable,
				text: 'View Template'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.editPrograms.canExecute,
				icon: 'times',
				permission: permissions.editPrograms.isAvailable,
				text: 'Delete Template'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onView,
		onDelete,
		permissions
	] );

	const columns = useMemo( () => [
		{
			id: 'templateName',
			accessor: row => `${ row.name }`,
			Header: 'Template Name',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'type',
			accessor: row => `${ row.type.name }`,
			Header: 'Type',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			id: 'status',
			accessor: row => `${ row.status.name }`,
			Header: 'Status',
			Cell: StatusCell,
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		}
	], [ StatusCell ] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewPrograms.isAvailable || permissions.editPrograms.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const newTemplate = useCallback( () => {
		const path = override.path
			? `${ override.path }/templates/0`
			: `/operations/programs/${ program.id }/templates/0`;

		history.push( path );
	}, [
		history,
		override,
		program.id
	] );

	const templateActions = useMemo( () => [
		{
			action: newTemplate,
			disable: !permissions.editPrograms.canExecute,
			icon: 'add',
			permission: permissions.editPrograms.isAvailable,
			text: 'New Template'
		}
	], [
		newTemplate,
		permissions
	] );

	const handleDeleteTemplate = useCallback( async () => {
		setTableLoading( true );

		try {
			await deleteTemplate( program?.id, templateToDelete.id );
			await retrieveTemplateList();
		} catch ( error ) {
			const errorMessage = handleError( error );

			setErrorAlertMessage( errorMessage );
			toggleErrorAlert();
		}

		setTableLoading( false );
	}, [
		templateToDelete.id,
		program,
		retrieveTemplateList,
		toggleErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to delete <strong>{ templateToDelete.name }</strong></span>, [ templateToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	if ( isLoading ) {
		return null;
	}

	return (
		<>
			<Actions actions={ [] } />
			{ log( 'templatesTable' ) }
			<PageHeader
				actions={ templateActions }
				title={
					<>
						<span>Templates</span>
						<HelpModal
							id={ override?.id === 'onboarding' ? 39 : 11 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			{ templates.length
				? <Table
					columns={ renderColumns }
					data={ templates }
					showCount={ templates.length > 10 }
					showEmptyRows={ false }
					showPagination={ templates.length > 10 }
				/>
				: tableLoading
					? <Loader active />
					: 'There are no templates available for this program.'
			}
			<Alert
				action={ handleDeleteTemplate }
				name="template-delete-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
			<Alert
				name="template-api-error-alert"
				heading="Unable to Process Request"
				message={ errorAlertMessage }
				open={ errorAlert }
				closeModal={ toggleErrorAlert }
				type="error"
			/>
		</>
	);
};

export default memo( TemplatesTable );
